import type { FieldErrors } from '@/types'

import { weekdayOf } from '@/lib/format'
import { WORKSHOP } from '@/constants/content'

// Shared by the forms in the browser and by the API routes, so both check exactly the same rules

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const text = (value: unknown, max: number): string => {
    if (typeof value !== 'string') return ''
    return value.trim().slice(0, max)
}

const whole = (value: unknown): number => {
    const number = Number(value)
    return Number.isFinite(number) ? Math.round(number) : 0
}

const checkName = (value: string): string | undefined =>
    value.length < 2 ? 'Please tell us your name.' : undefined

const checkEmail = (value: string): string | undefined =>
    EMAIL_PATTERN.test(value) ? undefined : 'Please enter a valid email address.'

/* Workshop booking */

export type BookingField = 'date' | 'guests' | 'fullName' | 'email'

export type BookingValues = {
    date: string
    guests: number
    fullName: string
    email: string
    company: string
}

export const EMPTY_BOOKING: BookingValues = {
    date: '',
    guests: 2,
    fullName: '',
    email: '',
    company: ''
}

export const validateBooking = (
    input: Record<string, unknown>,
    earliestDate: string,
    latestDate: string
): { values: BookingValues; errors: FieldErrors<BookingField> } => {
    const values: BookingValues = {
        date: text(input.date, 10),
        guests: whole(input.guests),
        fullName: text(input.fullName, 120),
        email: text(input.email, 160).toLowerCase(),
        company: text(input.company, 120)
    }

    const errors: FieldErrors<BookingField> = {}

    if (!DATE_PATTERN.test(values.date)) {
        errors.date = 'Please choose a date.'
    } else if (earliestDate && values.date < earliestDate) {
        errors.date = 'Please choose a date from tomorrow onwards.'
    } else if (latestDate && values.date > latestDate) {
        errors.date = 'We take bookings up to a year ahead.'
    } else if (weekdayOf(values.date) === WORKSHOP.closedWeekday) {
        errors.date = 'The atelier is closed on Mondays. Please choose another day.'
    }

    if (values.guests < 1 || values.guests > WORKSHOP.maxGuests) {
        errors.guests = `Choose between 1 and ${WORKSHOP.maxGuests} people.`
    }

    const nameError = checkName(values.fullName)
    if (nameError) errors.fullName = nameError

    const emailError = checkEmail(values.email)
    if (emailError) errors.email = emailError

    return { values, errors }
}

/* Contact inquiry */

export const CONTACT_TOPICS: Array<string> = [
    'A question about an order',
    'Wedding stationery',
    'Custom or wholesale paper',
    'Workshops and visits',
    'Press and collaborations',
    'Something else'
]

export type ContactField = 'fullName' | 'email' | 'topic' | 'message'

export type ContactValues = {
    fullName: string
    email: string
    topic: string
    message: string
    company: string
}

export const EMPTY_CONTACT: ContactValues = {
    fullName: '',
    email: '',
    topic: CONTACT_TOPICS[0],
    message: '',
    company: ''
}

export const validateContact = (input: Record<string, unknown>): { values: ContactValues; errors: FieldErrors<ContactField> } => {
    const values: ContactValues = {
        fullName: text(input.fullName, 120),
        email: text(input.email, 160).toLowerCase(),
        topic: text(input.topic, 80),
        message: text(input.message, 3000),
        company: text(input.company, 120)
    }

    const errors: FieldErrors<ContactField> = {}

    const nameError = checkName(values.fullName)
    if (nameError) errors.fullName = nameError

    const emailError = checkEmail(values.email)
    if (emailError) errors.email = emailError

    if (!CONTACT_TOPICS.includes(values.topic)) errors.topic = 'Please choose a topic.'
    if (values.message.length < 10) errors.message = 'Please write a little more, at least a sentence.'

    return { values, errors }
}

/* Order request */

export type OrderField = 'fullName' | 'email' | 'country' | 'notes' | 'items'

export type OrderValues = {
    fullName: string
    email: string
    country: string
    notes: string
    company: string
    items: Array<{ productId: string; quantity: number }>
}

export const EMPTY_ORDER: Omit<OrderValues, 'items'> = {
    fullName: '',
    email: '',
    country: '',
    notes: '',
    company: ''
}

export const MAX_QUANTITY = 20

export const validateOrder = (input: Record<string, unknown>): { values: OrderValues; errors: FieldErrors<OrderField> } => {
    const rawItems = Array.isArray(input.items) ? input.items.slice(0, 30) : []

    const items = rawItems
        .map((item: unknown) => {
            const entry = typeof item === 'object' && item !== null ? item as Record<string, unknown> : {}
            return { productId: text(entry.productId, 64), quantity: whole(entry.quantity) }
        })
        .filter(item => item.productId && item.quantity >= 1 && item.quantity <= MAX_QUANTITY)

    const values: OrderValues = {
        fullName: text(input.fullName, 120),
        email: text(input.email, 160).toLowerCase(),
        country: text(input.country, 80),
        notes: text(input.notes, 1500),
        company: text(input.company, 120),
        items
    }

    const errors: FieldErrors<OrderField> = {}

    const nameError = checkName(values.fullName)
    if (nameError) errors.fullName = nameError

    const emailError = checkEmail(values.email)
    if (emailError) errors.email = emailError

    if (values.country.length < 2) errors.country = 'Which country should we ship to?'
    if (values.items.length === 0) errors.items = 'Your cart is empty.'

    return { values, errors }
}
